import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Pencil, Upload, X, Image as ImageIcon, Eye } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import ErrorModal from '@/components/ErrorModal';

export default function Prizes() {
  const [prizes, setPrizes] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', image_url: '', cloudinary_public_id: '', status: 'pendiente', stage_id: '', assigned_to: '' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsPrize, setDetailsPrize] = useState(null);

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => { 
    Promise.all([
      api.get('/prizes'),
      api.get('/stages'),
      api.get('/users')
    ]).then(([p, s, u]) => {
      setPrizes(p.data);
      setStages(s.data);
      setUsers(u.data.filter(user => user.role === 'user'));
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const compressImage = async (file) => {
    const maxSizeMB = 3;
    const maxWidthOrHeight = 1024;
    
    // Validar tamaño original
    if (file.size > maxSizeMB * 1024 * 1024) {
      throw new Error(`La imagen debe ser de máximo ${maxSizeMB}MB`);
    }

    const options = {
      maxSizeMB: 0.14, // 150KB aproximadamente
      maxWidthOrHeight,
      useWebWorker: true,
      fileType: 'image/jpeg'
    };

    try {
      const compressedFile = await imageCompression(file, options);
      
      // Validar que no exceda 150KB después de compresión
      if (compressedFile.size > 150 * 1024) {
        toast.error('No se pudo comprimir la imagen a menos de 150KB. Usa una imagen más pequeña.');
        return null;
      }
      
      return compressedFile;
    } catch (error) {
      console.error('Error al comprimir imagen:', error);
      throw new Error('Error al comprimir la imagen');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Solo se permiten archivos JPG o PNG');
      return;
    }

    setUploading(true);
    try {
      // Comprimir imagen
      const compressedFile = await compressImage(file);
      if (!compressedFile) {
        setUploading(false);
        return;
      }

      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(compressedFile);

      // Subir a backend
      const formData = new FormData();
      formData.append('file', compressedFile);

      const response = await api.post('/prizes/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Cloudinary retorna: url, public_id, format, width, height, bytes
      setForm({ 
        ...form, 
        image_url: response.data.url,  // secure_url de Cloudinary
        cloudinary_public_id: response.data.public_id  // Para poder eliminar después
      });
      toast.success(`Imagen subida correctamente`);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Error al subir imagen');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, image_url: '', cloudinary_public_id: '' });
    setImagePreview(null);
  };

  const handleSave = async () => {
    const missing = [];
    if (!form.name) missing.push('Nombre del premio');
    if (!form.description) missing.push('Descripción');
    if (form.status === 'asignado' && !form.assigned_to) missing.push('Asignar a usuario');

    if (missing.length > 0) {
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: missing.map(f => `Falta: ${f}`)
      });
      return;
    }
    try {
      if (editing) { 
        await api.put(`/prizes/${editing}`, form); 
        toast.success('Premio actualizado'); 
      }
      else { 
        await api.post('/prizes', form); 
        toast.success('Premio creado'); 
      }
      setShowForm(false); 
      setEditing(null); 
      setForm({ name: '', description: '', image_url: '', cloudinary_public_id: '', status: 'pendiente', stage_id: '', assigned_to: '' }); 
      setImagePreview(null);
      load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar premio';
      setErrorModal({
        open: true,
        title: editing ? 'Error al actualizar premio' : 'Error al crear premio',
        message: errorMessage
      });
    }
  };

  const handleEdit = (p) => { 
    setEditing(p.id); 
    setForm({ 
      name: p.name, 
      description: p.description, 
      image_url: p.image_url || '',
      cloudinary_public_id: p.cloudinary_public_id || '',
      status: p.status,
      stage_id: p.stage_id || '',
      assigned_to: p.assigned_to || ''
    });
    if (p.image_url) {
      setImagePreview(p.image_url);
    }
    setShowForm(true); 
  };

  const handleDelete = async (id) => { 
    if (!window.confirm('¿Eliminar premio?')) return; 
    await api.delete(`/prizes/${id}`); 
    toast.success('Premio eliminado'); 
    load(); 
  };

  const statusColor = { 
    pendiente: 'bg-[#F4E39E]/20 text-[#F4E39E]', 
    asignado: 'bg-green-500/20 text-green-400', 
    anulado: 'bg-red-500/20 text-red-400' 
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-prizes-page">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Premios</h2>
        <button 
          onClick={() => { 
            setEditing(null); 
            setForm({ name: '', description: '', image_url: '', cloudinary_public_id: '', status: 'pendiente', stage_id: '', assigned_to: '' }); 
            setImagePreview(null);
            setShowForm(true); 
          }} 
          className="gold-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2" 
          data-testid="create-prize-button"
        >
          <Plus size={16} /> Crear
        </button>
      </div>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando...</div>
      ) : prizes.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">No hay premios registrados</div>
      ) : (
        <div className="glass-card overflow-hidden">
          {/* Header de tabla */}
          <div className="grid grid-cols-[2fr,1fr,1fr,1.5fr,auto] gap-4 p-4 bg-[#926F39]/10 border-b border-[#F4E39E]/20">
            <div className="text-[#F4E39E] font-bold text-sm">Premio</div>
            <div className="text-[#F4E39E] font-bold text-sm">Imagen</div>
            <div className="text-[#F4E39E] font-bold text-sm">Estado</div>
            <div className="text-[#F4E39E] font-bold text-sm">Usuario Ganador</div>
            <div className="text-[#F4E39E] font-bold text-sm text-right">Acciones</div>
          </div>

          {/* Filas de datos */}
          {prizes.map(p => (
            <div 
              key={p.id} 
              className="grid grid-cols-[2fr,1fr,1fr,1.5fr,auto] gap-4 p-4 border-b border-white/5 hover:bg-white/5 transition-colors items-center"
              data-testid={`prize-row-${p.id}`}
            >
              {/* Premio */}
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm mb-1 break-words">{p.name}</div>
                <div className="text-white/50 text-xs line-clamp-2 break-words">{p.description}</div>
                {p.stage && (
                  <div className="text-blue-400 text-xs mt-1">📍 {p.stage.name}</div>
                )}
              </div>

              {/* Imagen */}
              <div>
                {p.image_url ? (
                  <img 
                    src={p.image_url} 
                    alt={p.name}
                    className="w-16 h-16 object-cover rounded-lg border border-[#F4E39E]/30"
                  />
                ) : (
                  <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center">
                    <ImageIcon size={24} className="text-white/20" />
                  </div>
                )}
              </div>

              {/* Estado */}
              <div>
                <Badge className={statusColor[p.status] || 'bg-white/10 text-white/50'}>
                  {p.status}
                </Badge>
              </div>

              {/* Usuario Ganador */}
              <div>
                {p.assigned_user ? (
                  <div className="text-green-400 text-xs">
                    <div className="font-semibold">{p.assigned_user.user_id}</div>
                    {p.assigned_user.name && (
                      <div className="text-white/50">{p.assigned_user.name}</div>
                    )}
                  </div>
                ) : (
                  <span className="text-white/30 text-xs">Sin asignar</span>
                )}
              </div>

              {/* Acciones */}
              <div className="flex items-center gap-2 justify-end">
                <button 
                  onClick={() => { setDetailsPrize(p); setShowDetails(true); }} 
                  className="p-2 rounded hover:bg-white/10 text-white/50 hover:text-blue-400 transition-colors"
                  title="Ver detalles"
                  data-testid={`view-prize-${p.id}`}
                >
                  <Eye size={14} />
                </button>
                <button 
                  onClick={() => handleEdit(p)} 
                  className="p-2 rounded hover:bg-white/10 text-white/50 hover:text-[#F4E39E] transition-colors"
                  title="Editar premio"
                  data-testid={`edit-prize-${p.id}`}
                >
                  <Pencil size={14} />
                </button>
                <button 
                  onClick={() => handleDelete(p.id)} 
                  className="p-2 rounded hover:bg-red-500/10 text-white/50 hover:text-red-400 transition-colors"
                  title="Eliminar premio"
                  data-testid={`delete-prize-${p.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(o) => { if (!errorModal.open) setShowForm(o); }}>
        <DialogContent
          className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-md"
          onPointerDownOutside={(e) => { if (errorModal.open) e.preventDefault(); }}
          onInteractOutside={(e) => { if (errorModal.open) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (errorModal.open) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>
              {editing ? 'Editar Premio' : 'Crear Premio'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label className="text-white/60 text-sm">Nombre del premio <span className="text-red-400">*</span></label>
              <input 
                value={form.name} 
                onChange={e => setForm({ ...form, name: e.target.value })} 
                data-testid="prize-name-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" 
                placeholder="Ej: Rolex de 24K" 
              />
            </div>

            <div>
              <label className="text-white/60 text-sm">Descripción <span className="text-red-400">*</span></label>
              <textarea 
                value={form.description} 
                onChange={e => setForm({ ...form, description: e.target.value })} 
                rows={3} 
                data-testid="prize-desc-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none resize-none" 
                placeholder="Describe el premio..." 
              />
            </div>

            <div>
              <label className="text-white/60 text-sm">Imagen del premio</label>
              <div className="mt-1 space-y-2">
                {imagePreview ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded-lg border border-[#F4E39E]/30"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1 bg-red-500/80 hover:bg-red-500 rounded-full text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#F4E39E]/40 rounded-lg cursor-pointer hover:border-[#F4E39E] transition-colors bg-[#0A0F1C]">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-[#F4E39E]/60" />
                      <p className="text-xs text-white/60 text-center px-2">
                        {uploading ? 'Comprimiendo y subiendo...' : 'JPG/PNG • Máx 3MB • Auto-compresión a 150KB'}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm">Estado</label>
              <select 
                value={form.status} 
                onChange={e => {
                  const newStatus = e.target.value;
                  setForm({ 
                    ...form, 
                    status: newStatus,
                    // Limpiar asignación si el estado deja de ser "asignado"
                    assigned_to: newStatus === 'asignado' ? form.assigned_to : ''
                  });
                }}
                data-testid="prize-status-select"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none"
              >
                <option value="pendiente">Pendiente</option>
                <option value="asignado">Asignado</option>
                <option value="anulado">Anulado</option>
              </select>
            </div>

            <div>
              <label className="text-white/60 text-sm">Etapa (opcional)</label>
              <select 
                value={form.stage_id} 
                onChange={e => setForm({ ...form, stage_id: e.target.value })}
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none"
              >
                <option value="">-- Sin etapa --</option>
                {stages.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {form.status === 'asignado' && (
              <div data-testid="assigned-to-wrapper">
                <label className="text-white/60 text-sm">
                  Asignar a usuario <span className="text-red-400">*</span>
                </label>
                <select 
                  value={form.assigned_to} 
                  onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                  data-testid="prize-assigned-select"
                  className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none"
                >
                  <option value="">-- Selecciona un usuario --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.user_id} {u.name && `- ${u.name}`}</option>
                  ))}
                </select>
              </div>
            )}

            <button 
              onClick={handleSave} 
              className="gold-btn w-full py-3 rounded-lg font-bold" 
              data-testid="save-prize-button"
            >
              Guardar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalles del Premio */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="prize-details-modal">
          <DialogHeader>
            <DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>
              Detalles del Premio
            </DialogTitle>
          </DialogHeader>
          {detailsPrize && (
            <div className="space-y-4">
              {detailsPrize.image_url && (
                <div className="flex justify-center">
                  <img
                    src={detailsPrize.image_url}
                    alt={detailsPrize.name}
                    className="max-h-56 w-auto object-contain rounded-lg border border-[#F4E39E]/30"
                  />
                </div>
              )}

              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Nombre</div>
                <div className="text-white font-semibold text-base break-words">{detailsPrize.name}</div>
              </div>

              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Descripción</div>
                <div className="text-white/90 text-sm break-words whitespace-pre-wrap max-h-40 overflow-y-auto pr-1">
                  {detailsPrize.description || <span className="text-white/30">Sin descripción</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Fase</div>
                  <div className="text-blue-400 text-sm">
                    {detailsPrize.stage?.name || <span className="text-white/30">Sin etapa</span>}
                  </div>
                </div>
                <div>
                  <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Estado</div>
                  <Badge className={statusColor[detailsPrize.status] || 'bg-white/10 text-white/50'}>
                    {detailsPrize.status}
                  </Badge>
                </div>
              </div>

              <div>
                <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Usuario asignado</div>
                {detailsPrize.assigned_user ? (
                  <div className="text-green-400 text-sm">
                    <span className="font-semibold">{detailsPrize.assigned_user.user_id}</span>
                    {detailsPrize.assigned_user.name && (
                      <span className="text-white/60"> — {detailsPrize.assigned_user.name}</span>
                    )}
                  </div>
                ) : (
                  <div className="text-white/30 text-sm">Sin asignar</div>
                )}
              </div>

              <button
                onClick={() => {
                  setShowDetails(false);
                  handleEdit(detailsPrize);
                }}
                className="gold-btn w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2"
                data-testid="details-edit-button"
              >
                <Pencil size={16} /> Editar Premio
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
